import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
	BeakerIcon,
	CheckCircleIcon,
	ChevronRightIcon,
	ExclamationTriangleIcon,
	QrCodeIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';

import Modal from '../../components/Shell/Modal';
import DataTable from '../../components/DataTable';
import { MainContent } from '../../components/Shell/Content';
import BetaAttributes from '../../components/Attributes';
import {
	AttributeRow,
	TemplateReactSelect,
} from '../../components/Attributes/Attributes_old';
import { AvatarGenerated } from '../../components/Avatar';
import { getUserFullName } from '../../components/helpers/functions';
import Button from '../../components/Button';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import Banner from '../../components/Banner';

import useAsync from '../../hooks/useAsync';
import useRequired from '../../hooks/useRequired';
import usePrevious from '../../hooks/usePrevious';

import {
	addInventoryToPicklist,
	addPicklist,
	getPicklist,
	getPicklists,
	removeInventoryFromPicklist,
	updatePicklistStatus,
} from '../../api/picklists';

import {
	getAllStoragePointsConsumables,
	getStoragePoint,
	getStoragePoints,
} from '../../api/storage-points';
import { getConsumables } from '../../api/consumables';

const publishingOptions = [
	{
		title: 'Published',
		description: 'This job posting can be viewed by anyone who has the link.',
		current: true,
	},
	{
		title: 'Draft',
		description: 'This job posting will no longer be publicly accessible.',
		current: false,
	},
];

const formatStorageOptionLabel = ({ value, label, storagePoint }) => {
	return (
		<div className="flex justify-between items-center cursor-pointer">
			<div>{label}</div>
			<div className="text-gray-400 text-xxs pl-3 truncate"></div>
		</div>
	);
};

const formatOptionLabel = ({ value, label, consumable, inventory }) => {
	return (
		<div className="flex justify-between items-center cursor-pointer">
			<div>{label}</div>
			<div className="text-gray-400 text-xxs pl-3 truncate">
				{inventory?.shelf?.storage_point?.name
					? `${inventory?.shelf?.storage_point?.name} - `
					: ''}
				{inventory?.shelf?.storage_point?.location?.name}
			</div>
		</div>
	);
};

const Picklists = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [modalOpen, setModalOpen] = useState(null);
	const [selected, setSelected] = useState(publishingOptions[0]);
	const [isAddConsumableOpen, setIsAddConsumableOpen] = useState(null);
	const [addConsumableForm, setAddConsumableForm] = useState({});
	const [isSelectStoragePointOpen, setIsSelectStoragePointOpen] =
		useState(null);
	const [selectStorageForm, setSelectStorageForm] = useState({});
	const [removalForm, setRemovalForm] = useState({});

	const { value: picklist, execute: onRefreshPicklist } = useAsync(
		getPicklist,
		{ immediate: false }
	);

	const { value: picklists, execute: onRefreshPicklists } =
		useAsync(getPicklists);

	const {
		value: inventory,
		execute: onRefreshInventory,
		status: isLoadingInventory,
	} = useAsync(getAllStoragePointsConsumables, {
		immediate: false,
		defaultPayload: { trackInventory: 'both' },
	});

	const inventoryOptions = inventory
		?.sort((a, b) => a?.consumable?.name?.localeCompare(b?.consumable?.name))
		.map((inventory) => ({
			label: inventory?.consumable?.name,
			value: inventory?.id,
			consumable: inventory?.consumable,
			inventory,
		}));

	const {
		value: storagePoints,
		execute: onRefreshStoragePoints,
		status: isLoadingStoragePoints,
	} = useAsync(getStoragePoints, {
		immediate: false,
	});

	const { execute: onCreatePicklist, status: isLoadingPicklist } = useAsync(
		addPicklist,
		{ immediate: false }
	);

	const {
		value: storagePoint,
		execute: onRefreshStoragePoint,
		status: isLoadingStoragePoint,
		resetValue: onResetStoragePoint,
	} = useAsync(getStoragePoint, { immediate: false });

	const {
		execute: onAddInventoryToPicklist,
		status: isAddingInventoryToPicklist,
	} = useAsync(addInventoryToPicklist, { immediate: false });

	const {
		execute: onRemoveInventoryFromPicklist,
		status: isRemovingInventoryFromPicklist,
	} = useAsync(removeInventoryFromPicklist, { immediate: false });

	const { execute: onUpdatePicklistStatus, status: isUpdatingStatus } =
		useAsync(updatePicklistStatus, { immediate: false });

	const {
		value: consumables,
		execute: onRefreshConsumables,
		status: isLoadingConsumables,
	} = useAsync(getConsumables, { immediate: false });

	const picklistInventoriesByStoragePoint =
		picklist?.picklist_inventories?.reduce((o, a) => {
			const storagePoint = a?.inventory?.shelf?.storage_point;
			if (o[storagePoint?.id]) {
				o[storagePoint?.id].push(a);
			} else {
				o[storagePoint?.id] = [a];
			}
			return o;
		}, {});

	const picklistInventoriesByConsumable =
		picklist?.picklist_inventories?.reduce((o, a) => {
			const consumable = a?.inventory?.consumable;
			if (o[consumable?.id]) {
				o[consumable?.id].push(a);
			} else {
				o[consumable?.id] = [a];
			}
			return o;
		}, {});

	const previousId = usePrevious(id);

	useEffect(() => {
		if (id) {
			onRefreshPicklist({ id });
			setModalOpen({ id });
			onRefreshInventory();
			onRefreshStoragePoints();
			onRefreshConsumables({ pageSize: null });
		}

		if (previousId && !id) {
			setRemovalForm({});
			onRefreshPicklists();
		}
	}, [id]);

	useEffect(() => {
		if (id && picklist?.status === 'review') {
			let updatedForm = {};

			Object.keys(picklistInventoriesByConsumable || {}).map((key) => {
				const totalNeeded = picklistInventoriesByConsumable[key]?.reduce(
					(o, a) => o + (a?.quantity || 0),
					0
				);

				const consumableId =
					picklistInventoriesByConsumable[key][0]?.inventory?.consumable?.id;

				const consumable = consumables?.rows?.find(
					(o) => o.id === consumableId
				);

				const trackedInventory = consumable?.inventories?.filter(
					(invent) => invent?.trackInventory
				);

				trackedInventory
					?.sort((a, b) => b.quantity - a.quantity)
					.map((track) => {
						const newValue =
							track?.quantity <=
							totalNeeded - (updatedForm[track?.id]?.value || 0)
								? track?.quantity
								: totalNeeded - (updatedForm[track?.id]?.value || 0);
						updatedForm[track?.id] = { value: newValue || 0 };
					});
			});

			setRemovalForm({ ...removalForm, ...updatedForm });
		}
	}, [id, picklist?.status, consumables]);

	useEffect(() => {
		if (isAddConsumableOpen?.inventoryId) {
			setAddConsumableForm({
				consumable: {
					value: (inventoryOptions || []).find(
						(o) => o?.inventory?.id === isAddConsumableOpen?.inventoryId
					),
				},
			});
		}
	}, [isAddConsumableOpen?.inventoryId]);

	useEffect(() => {
		if (selectStorageForm?.storagePoint?.value) {
			onRefreshStoragePoint({
				id: selectStorageForm?.storagePoint?.value?.value,
			});
		} else {
			onResetStoragePoint();
		}
	}, [selectStorageForm?.storagePoint?.value]);

	return (
		<div>
			<DataTable
				columns={[
					{
						header: 'NAME',
						accessorKey: 'name',
						cell: (info) => {
							return (
								info?.getValue() ||
								`${moment(info?.row?.original?.createdAt).format(
									'dddd, MMM Do, YYYY'
								)}`
							);
						},
					},
					{
						header: 'STATUS',
						accessorKey: 'status',
						cell: (info) => {
							return {
								active: 'Active',
								complete: 'Completed',
								review: 'In review',
							}[info?.getValue()];
						},
					},
					{
						header: 'CREATED BY',
						accessorKey: 'user',
						cell: (info) => {
							return getUserFullName(info?.getValue()) || null;
						},
					},
					{
						header: 'LAST UPDATED',
						accessorKey: 'updatedAt',
						enableSorting: false,

						cell: (info) => {
							return info?.row?.original?.isArchived
								? null
								: moment(info?.getValue()).fromNow();
						},
					},
				]}
				actions={[
					{
						label: 'Start Picklist',
						onClick: () =>
							onCreatePicklist(null, (response) =>
								navigate(`/consumables/picklists/${response.id}`)
							),
					},
				]}
				data={{ rows: picklists || [] }}
				onRow={(row) => {
					navigate(`/consumables/picklists/${row?.original?.id}`);
				}}
				usePagination={false}
				hasCheckboxes={false}
				manualSorting={false}
			/>
			<Modal
				open={modalOpen}
				size="full"
				onClose={() => {
					setModalOpen(null);
					navigate(`/consumables/picklists`);
				}}
			>
				<MainContent
					banners={[
						picklist?.status === 'complete'
							? {
									status: 'success',
									message: (
										<div className="flex items-center">
											<CheckCircleIcon className="w-4 h-4 text-green-500 mr-3" />
											This picklist is have been completed by{' '}
											<div className="flex items-center ml-1">
												<AvatarGenerated
													size="xxs"
													id={getUserFullName(picklist?.user)}
													type="person"
												/>
												<div className="ml-1">
													{getUserFullName(picklist?.user)}
												</div>
											</div>
										</div>
									),
							  }
							: {
									status: 'default',
									message: (
										<div className="flex items-center">
											<ExclamationTriangleIcon className="w-4 h-4 text-gray-500 mr-3" />
											This picklist is actively being worked on by{' '}
											<div className="flex items-center ml-1">
												<AvatarGenerated
													size="xxs"
													id={getUserFullName(picklist?.user)}
													type="person"
												/>
												<div className="ml-1">
													{getUserFullName(picklist?.user)}
												</div>
											</div>
										</div>
									),
							  },
					]}
					maxWidth="max-w-2xl"
				>
					<div className="w-full py-4">
						<div className="flex justify-between items-center">
							<div className="text-3xl">Picklist</div>
							<div>
								{picklist?.status === 'active' && (
									<Button
										disabled={
											!Object.keys(picklistInventoriesByStoragePoint || {})
												.length
										}
										onClick={() =>
											onUpdatePicklistStatus({ id, status: 'review' }, () =>
												onRefreshPicklist({ id })
											)
										}
									>
										Next: Review
									</Button>
								)}

								{picklist?.status === 'review' && (
									<>
										<Button
											type="secondary"
											onClick={() =>
												onUpdatePicklistStatus({ id, status: 'active' }, () =>
													onRefreshPicklist({ id })
												)
											}
										>
											Back
										</Button>
										<Button
											disabled={
												!Object.keys(picklistInventoriesByConsumable || {})
													.length
											}
											onClick={() =>
												onUpdatePicklistStatus(
													{
														id,
														status: 'complete',
														picklist: Object.keys(removalForm).map((key) => ({
															id: key,
															value: removalForm[key]?.value,
														})),
													},
													() => onRefreshPicklist({ id })
												)
											}
										>
											Submit Picklist
										</Button>
									</>
								)}
							</div>
						</div>
					</div>
					<div className="border-b mb-4 pb-3 text-xs text-gray-500 py-3 flex justify-between items-center">
						<div className="space-y-3">
							<div>
								<span className="font-bold">Created on</span>{' '}
								{moment(picklist?.createdAt).format('MMM Do, YYYY [at] h:mmA')}
								{picklist?.createdAt !== picklist?.updatedAt
									? ` - Updated ${moment(picklist?.updatedAt).fromNow()}`
									: null}
							</div>
						</div>
					</div>

					{picklist?.status === 'active' && (
						<>
							<div className="flex justify-end items-center">
								<Button
									type="secondary"
									onClick={() => setIsSelectStoragePointOpen(true)}
								>
									Find a Storage Point
								</Button>
								<Button
									type="secondary"
									onClick={() => setIsAddConsumableOpen(true)}
								>
									Find a Consumable
								</Button>
								<Button type="secondary">
									<QrCodeIcon className="w-5 h-5 text-gray-600 cursor-pointer" />
								</Button>
							</div>
							<div>
								{Object.keys(picklistInventoriesByStoragePoint || {}).map(
									(key) => {
										const storagePoint =
											picklistInventoriesByStoragePoint[key][0]?.inventory
												?.shelf?.storage_point;

										return (
											<>
												<div class="border-t border-gray-200 mt-8">
													<div class="bg-gray-50 py-2 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">
														{storagePoint?.name || 'N/A'}
														<span className="text-gray-400 font-normal">
															{storagePoint?.location?.nam
																? ` - ${storagePoint?.location?.nam}`
																: ''}
														</span>
													</div>
												</div>
												<ul role="list" className="divide-y divide-gray-100">
													{picklistInventoriesByStoragePoint[key]?.map(
														(inventory) => (
															<li key={inventory.id} className="relative py-5">
																<div className="px-4 sm:px-6 lg:px-8">
																	<div className="mx-auto flex max-w-4xl justify-between gap-x-6">
																		<div className="flex min-w-0 gap-x-4">
																			{inventory?.inventory?.consumable
																				?.image ? (
																				<img
																					className="h-12 w-12 flex-none rounded-full bg-gray-50"
																					src={
																						inventory?.inventory?.consumable
																							?.image
																					}
																					alt=""
																				/>
																			) : (
																				<div className="h-12 w-12 flex-none rounded-full bg-gray-50 flex justify-center items-center">
																					<BeakerIcon className="w-5 h-5 text-gray-400" />
																				</div>
																			)}
																			<div className="min-w-0 flex-auto">
																				<p className="text-sm font-semibold leading-6 text-gray-900">
																					{
																						inventory?.inventory?.consumable
																							?.name
																					}
																				</p>
																				<p className="mt-1 flex text-xs leading-5 text-gray-500">
																					{`Quantity needed: ${
																						inventory.quantity || 0
																					}`}
																				</p>
																			</div>
																		</div>
																		<div className="flex shrink-0 items-center gap-x-4">
																			<div className="hidden sm:flex sm:flex-col sm:items-end">
																				<Button
																					type="secondary"
																					onClick={() =>
																						onRemoveInventoryFromPicklist(
																							{
																								id,
																								picklistInventoryId:
																									inventory?.id,
																							},
																							() => {
																								onRefreshPicklist({ id });
																							}
																						)
																					}
																				>
																					Remove
																				</Button>
																			</div>
																		</div>
																	</div>
																</div>
															</li>
														)
													)}
												</ul>
											</>
										);
									}
								)}
							</div>
						</>
					)}

					{picklist?.status === 'review' && (
						<div>
							{Object.keys(picklistInventoriesByConsumable || {}).map((key) => {
								const totalNeeded = picklistInventoriesByConsumable[
									key
								]?.reduce((o, a) => o + (a?.quantity || 0), 0);

								const consumableId =
									picklistInventoriesByConsumable[key][0]?.inventory?.consumable
										?.id;

								const consumable = consumables?.rows?.find(
									(o) => o.id === consumableId
								);

								const trackedInventory = consumable?.inventories?.filter(
									(invent) => invent?.trackInventory
								);

								let total = 0;
								let max = 0;

								return (
									<>
										<div class="border-t border-gray-200 mt-8">
											<div class="bg-gray-50 py-2 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">
												{consumable?.name || 'N/A'}
												<span className="text-gray-400 font-normal">{}</span>
											</div>
										</div>

										{!trackedInventory?.length ? (
											<p className="text-center text-gray-500 text-xs py-4">
												No tracked inventory
											</p>
										) : (
											<>
												<ul role="list" className="divide-y divide-gray-100">
													{trackedInventory
														?.sort((a, b) => b.quantity - a.quantity)
														.map((track) => {
															total =
																total +
																parseInt(removalForm[track?.id]?.value || 0);

															max = max + track?.quantity;

															return (
																<li key={track.id} className="relative py-5">
																	<div className="px-4 sm:px-6 lg:px-8">
																		<div className="mx-auto flex max-w-4xl justify-between gap-x-6">
																			<div className="flex min-w-0 gap-x-4">
																				<div className="min-w-0 flex-auto">
																					<p className="text-sm font-semibold leading-6 text-gray-900">
																						{track?.shelf?.storage_point?.name}
																					</p>
																					<p className="mt-1 flex text-xs leading-5 text-gray-500">
																						{`Quantity on-hand: ${track?.quantity}`}
																					</p>
																				</div>
																			</div>
																			<div className="flex shrink-0 items-center gap-x-4">
																				<div className="hidden sm:flex sm:flex-col sm:items-end">
																					<BetaAttributes
																						field={{
																							type: 'numeric',
																							key: track?.id,
																						}}
																						onChange={(index, value) => {
																							if (
																								parseInt(value) >= 0 &&
																								parseInt(value) <=
																									track?.quantity &&
																								parseInt(value) <= totalNeeded
																							) {
																								setRemovalForm({
																									...removalForm,
																									[index]: { value },
																								});
																							}
																						}}
																						form={{
																							[track?.id]: { value: 0 },
																							...removalForm,
																						}}
																					/>
																				</div>
																			</div>
																		</div>
																	</div>
																</li>
															);
														})}
												</ul>
												<div className="flex justify-end pr-8 text-sm text-gray-500">
													{`Picking: ${parseInt(
														total || 0
													)} out of ${totalNeeded}`}
												</div>

												{max === total && total !== totalNeeded ? (
													<Banner
														status="error"
														size="xs"
														className="mt-5 rounded-lg"
													>
														<div className="flex items-center justify-between">
															<div>
																You do not have enough quantity to complete this
																request
															</div>
															<Button type="ghost">Submit a Request</Button>
														</div>
													</Banner>
												) : null}
											</>
										)}
									</>
								);
							})}
						</div>
					)}

					{picklist?.status === 'complete' && (
						<>
							<h3 className="text-base font-semibold text-xs text-gray-900">
								Picked:
							</h3>

							<h3 className="text-base font-semibold text-xs text-gray-900">
								Added to:
							</h3>
						</>
					)}

					<Modal
						open={isSelectStoragePointOpen}
						onClose={() => {
							setIsSelectStoragePointOpen(null);
							setSelectStorageForm({});
						}}
					>
						{' '}
						<div className="grid grid-cols-2 space-y-6 mb-4">
							<div className="col-span-2 flex">
								<div className="flex-1">
									<TemplateReactSelect
										name={'Storage Point'}
										dataIndex={'storagePoint'}
										options={storagePoints?.rows
											?.sort((a, b) => a?.name?.localeCompare(b?.name))
											.map((storagePoint) => ({
												label: storagePoint?.name,
												value: storagePoint?.id,
												storagePoint,
											}))}
										value={selectStorageForm?.storagePoint?.value || ''}
										onChange={(index, value) => {
											if (value) {
												setSelectStorageForm({
													...selectStorageForm,
													[index]: { value },
												});
											} else {
												let updatedForm = { ...selectStorageForm };
												delete updatedForm[index];
												setSelectStorageForm(updatedForm);
											}
										}}
										disabled={isLoadingStoragePoints === 'pending'}
										formatOptionLabel={formatStorageOptionLabel}
									/>
								</div>
								{selectStorageForm?.storagePoint ? (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<XCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() => {
												let updatedForm = { ...selectStorageForm };
												delete updatedForm['storagePoint'];
												setSelectStorageForm(updatedForm);
											}}
										/>
									</div>
								) : null}{' '}
							</div>

							<div className="col-span-2 mt-4">
								{isLoadingStoragePoint === 'pending' ? (
									<LoadingSkeleton />
								) : selectStorageForm?.storagePoint?.value && storagePoint ? (
									<AttributeRow
										name={`Consumables Found (${
											storagePoint?.shelves?.flatMap(
												(shelf) => shelf?.inventories
											)?.length || 0
										})`}
									>
										<ul role="list" className="divide-y divide-gray-100">
											{storagePoint?.shelves
												?.flatMap((shelf) => shelf?.inventories)
												?.map((inventory) => (
													<div>
														<li
															key={inventory.id}
															className="relative py-2 hover:bg-gray-50 cursor-pointer"
															onClick={() =>
																setIsAddConsumableOpen({
																	inventoryId: inventory?.id,
																})
															}
														>
															<div className="px-4">
																<div className="mx-auto flex max-w-4xl justify-between gap-x-6">
																	<div className="flex min-w-0 gap-x-4 items-center">
																		<div className="min-w-0 flex-auto">
																			<p className="text-xs text-gray-900">
																				<span className="absolute inset-x-0 -top-px bottom-0" />
																				{inventory?.consumable?.name}
																			</p>
																		</div>
																	</div>
																	<div className="flex shrink-0 items-center gap-x-4">
																		<ChevronRightIcon
																			className="h-4 w-4 flex-none text-gray-400"
																			aria-hidden="true"
																		/>
																	</div>
																</div>
															</div>
														</li>
													</div>
												))}
										</ul>
									</AttributeRow>
								) : null}
							</div>
						</div>
					</Modal>

					<Modal
						title="Add to Picklist"
						open={isAddConsumableOpen}
						onClose={() => {
							setIsAddConsumableOpen(null);
							setAddConsumableForm({});
						}}
					>
						<div className="grid grid-cols-2 space-y-6 mb-4">
							<div className="col-span-2 flex">
								<div className="flex-1">
									<TemplateReactSelect
										name={'Consumable'}
										dataIndex={'consumable'}
										options={inventoryOptions}
										value={addConsumableForm?.consumable?.value || ''}
										onChange={(index, value) => {
											if (value) {
												setAddConsumableForm({
													...addConsumableForm,
													[index]: { value },
												});
											} else {
												let updatedForm = { ...addConsumableForm };
												delete updatedForm[index];
												setAddConsumableForm(updatedForm);
											}
										}}
										disabled={
											isLoadingInventory === 'pending' ||
											isAddConsumableOpen?.inventoryId
										}
										formatOptionLabel={formatOptionLabel}
									/>
								</div>
								{addConsumableForm?.consumable &&
								!isAddConsumableOpen?.inventoryId ? (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<XCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() => {
												let updatedForm = { ...addConsumableForm };
												delete updatedForm['consumable'];
												setAddConsumableForm(updatedForm);
											}}
										/>
									</div>
								) : null}{' '}
							</div>

							<AttributeRow name="Quantity">
								<div className="col-span-2">
									<div className="w-[50%] m-auto flex items-center">
										<Button
											type="secondary"
											onClick={() =>
												parseInt(addConsumableForm?.quantity?.value || 0) > 0
													? setAddConsumableForm({
															...addConsumableForm,
															quantity: {
																value:
																	parseInt(
																		addConsumableForm?.quantity?.value || 0
																	) - 1,
															},
													  })
													: {}
											}
										>
											-
										</Button>
										<div className="ml-3 -mt-3">
											<BetaAttributes
												field={{
													type: 'numeric',
													key: 'quantity',
												}}
												onChange={(index, value) => {
													if (parseInt(value) >= 0) {
														setAddConsumableForm({
															...addConsumableForm,
															[index]: { value },
														});
													}
												}}
												form={{ quantity: { value: 0 }, ...addConsumableForm }}
											/>
										</div>
										<Button
											type="secondary"
											onClick={() =>
												setAddConsumableForm({
													...addConsumableForm,
													quantity: {
														value:
															parseInt(
																addConsumableForm?.quantity?.value || 0
															) + 1,
													},
												})
											}
										>
											+
										</Button>
									</div>
								</div>
							</AttributeRow>

							<div className="flex justify-end col-span-2">
								<Button
									disabled={
										!useRequired(addConsumableForm, ['consumable']) ||
										!addConsumableForm?.quantity?.value
									}
									onClick={() => {
										onAddInventoryToPicklist(
											{
												id,
												inventoryId:
													addConsumableForm?.consumable?.value?.inventory?.id,
												quantity: addConsumableForm?.quantity?.value || 0,
											},
											(response) => {
												setIsAddConsumableOpen(null);
												setAddConsumableForm({});
												onRefreshPicklist({ id });
											}
										);
									}}
								>
									Add to Picklist
								</Button>
							</div>
						</div>
					</Modal>
				</MainContent>
			</Modal>
		</div>
	);
};

export default Picklists;
